import React, { useCallback, useEffect, useRef, useState } from 'react';
import { withOktaAuth, useOktaAuth } from '@okta/okta-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import config from './config';
import { useSecureOktaUser } from './hooks/useSecureOktaUser';
import { useBearerFetch } from './hooks/useBearerFetch';

function NavbarFunctional() {
  const { oktaAuth } = useOktaAuth();
  const { user, isAuthenticated } = useSecureOktaUser();
  const bearerFetch = useBearerFetch();
  const [isAdmin, setIsAdmin] = useState(false);
  const [activeMobile, setActiveMobile] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [width, setWidth] = useState(window.innerWidth);
  const [route, setRoute] = useState(window.location.pathname);
  const [userPhoto, setUserPhoto] = useState('img/default_profile_photo.png');
  const wrapperRef = useRef(null);
  const photoWrapperRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    setRoute(location.pathname);
    // Close profile menu when navigation occurs
    setShowProfileMenu(false);
  }, [location]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (photoWrapperRef.current && !photoWrapperRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };

    if (showProfileMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showProfileMenu]);

  // Resize listener
  useEffect(() => {
    const onResize = () => {
      const newWidth = window.innerWidth;
      setWidth(newWidth);
      if (newWidth >= 766) {
        setActiveMobile(false);
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Charger si admin
  useEffect(() => {
    let abort = false;
    const checkAdmin = async () => {
      if (!isAuthenticated || !user) return;
      navigate('/');
      try {
        const res = await bearerFetch(`${config.env.apiBaseUrl}/isCurrentUserAdmin`);
        if (res.ok) {
          const data = await res.json();
          if (!abort) setIsAdmin(data === true);
        }
      } catch (e) {
        // silencieux
      }
    };
    checkAdmin();
    return () => { abort = true; };
  }, [isAuthenticated, user, bearerFetch]);

  // Load user photo
  useEffect(() => {
    let abort = false;
    const loadPhoto = async () => {
      if (!isAuthenticated || !user) return;
      try {
        const url = `${config.env.apiBaseUrl}/get-user-photo?userId=${user.preferred_username}&size=240x240`;
        const res = await bearerFetch(url);
        if (!res.ok) return;
        const data = await res.json();
        if (!abort && data?.image) {
          setUserPhoto(data.image);
        }
      } catch (e) {
        // noop
      }
    };
    loadPhoto();
    return () => { abort = true; };
  }, [isAuthenticated, user, bearerFetch]);

  const login = useCallback(() => {
    oktaAuth.signInWithRedirect();
  }, [oktaAuth]);

  const logout = useCallback(() => {
    oktaAuth.signOut('/');
  }, [oktaAuth]);

  const toggleMobile = () => {
    setActiveMobile(a => !a);
    // Always close profile menu when opening mobile menu
    setShowProfileMenu(false);
  };

  const toggleProfileMenu = (event) => {
    event.stopPropagation(); // Prevent event bubbling
    setShowProfileMenu(v => !v);
    // Close mobile menu if open
    if (activeMobile) setActiveMobile(false);
  };

  return (
    <div>
      {(!isAuthenticated) &&
        <nav className='navbar navbar-expand-lg navbar-light bg-white' role="banner">
          <div className="container">
            <Link to="/"><img className="logoengie" src="img/logo-engie.svg" alt="ENGIE" /></Link>
            <div className="navbar-nav d-none d-md-flex ml-auto">
              <button
                className="btn btn-login-modern"
                onClick={login}
              >
                <FormattedMessage id="navbar.login" defaultMessage="Login" />
              </button>
            </div>
          </div>
        </nav>
      }
      {(isAuthenticated) &&
        <nav role="banner" className={activeMobile && width < 992 ? 'navbar navbar-expand-lg navbar-light bg-white mobile-menu-active' : 'navbar navbar-expand-lg navbar-light bg-white'}>
          <div className="container">
            <Link to="/"><img className="logoengie" src="img/logo-engie.svg" alt="ENGIE" /></Link>
            <button ref={wrapperRef} className={activeMobile ? 'navbar-toggler active d-md-none' : 'navbar-toggler d-md-none'} type="button" onClick={toggleMobile}><span className="navbar-toggler-icon"></span></button>
            <div className="mobile-menu-overlay" onClick={() => setActiveMobile(false)}></div>
            <div ref={photoWrapperRef} className={`navbar-collapse${activeMobile ? ' show' : ''} d-md-flex justify-content-end`}>
              <ul className="navbar-nav margin-rigth-nav heigth-0">
                <li className={route === '/' ? 'nav-item active' : 'nav-item'}><Link to="/" className="nav-link" onClick={() => { setRoute('/'); setShowProfileMenu(false); }}> <FormattedMessage id="navbar.home" defaultMessage="Home" /> </Link></li>
                <li className={route === '/Result' ? 'nav-item active' : 'nav-item'}><Link to="/Result" className="nav-link" onClick={() => { setRoute('/Result'); setShowProfileMenu(false); }}> <FormattedMessage id="navbar.directory" defaultMessage="Directory" /> </Link></li>
                {isAdmin && (
                  <li className={route === '/Settings' ? 'nav-item active' : 'nav-item'}><Link to="/Settings" className="nav-link" onClick={() => { setRoute('/Settings'); setShowProfileMenu(false); }}><FormattedMessage id="navbar.settings" defaultMessage="Settings" /></Link></li>
                )}
                <li className="nav-item profile-dropdown" onClick={toggleProfileMenu}>
                  <img src={userPhoto} id="LoggedUser" className="profile-img-navbar" alt="" role="button" />
                  <div className={showProfileMenu ? 'dropdown-menu show elevated' : 'dropdown-menu elevated'} aria-labelledby="navbarDropdown">
                    <Link to="/myprofile" className="dropdown-item" onClick={() => { setRoute('/myprofile'); setShowProfileMenu(false); }}> <FormattedMessage id="navbar.profile" defaultMessage="My profile" /> </Link>
                    <a href="# " onClick={(e) => { e.preventDefault(); logout(); setShowProfileMenu(false); }} className="dropdown-item d-none d-md-block"> <FormattedMessage id="navbar.logout" defaultMessage="Logout" /> </a>
                  </div>
                </li>
                <li className={route === '/myprofile' ? 'nav-item active d-md-none' : 'nav-item d-md-none'}><Link to="/myprofile" className="nav-link" onClick={() => { setRoute('/myprofile'); setShowProfileMenu(false); }}> <FormattedMessage id="navbar.profile" defaultMessage="My profile" /> </Link></li>
                <li className="nav-item d-md-none"><a href="# " onClick={(e) => { e.preventDefault(); logout(); setShowProfileMenu(false); }} className="nav-link"> <FormattedMessage id="navbar.logout" defaultMessage="Logout" /> </a></li>
              </ul>
            </div>
          </div>
          {activeMobile &&
            <div className="mobile-menu-sidebar">
              <div className="mobile-menu-header">
                <div className="mobile-menu-user">
                  <img src={userPhoto} id="LoggedUserMobile" className="profile-img-navbar" alt="" />
                  <span>{user?.name || 'User'}</span>
                </div>
              </div>
              <ul className="mobile-menu-nav">
                <li><Link to="/" onClick={() => { setRoute('/'); setActiveMobile(false); }}> <FormattedMessage id="navbar.home" defaultMessage="Home" /> </Link></li>
                <li><Link to="/Result" onClick={() => { setRoute('/Result'); setActiveMobile(false); }}> <FormattedMessage id="navbar.directory" defaultMessage="Directory" /> </Link></li>
                {isAdmin && <li><Link to="/Settings" onClick={() => { setRoute('/Settings'); setActiveMobile(false); }}><FormattedMessage id="navbar.settings" defaultMessage="Settings" /></Link></li>}
                <li><Link to="/myprofile" onClick={() => { setRoute('/myprofile'); setActiveMobile(false); }}> <FormattedMessage id="navbar.profile" defaultMessage="My profile" /> </Link></li>
                <li><a href="# " onClick={(e) => { e.preventDefault(); logout(); setActiveMobile(false); }}> <FormattedMessage id="navbar.logout" defaultMessage="Logout" /> </a></li>
              </ul>
            </div>
          }
        </nav>
      }
    </div>
  );
}

export default withOktaAuth(NavbarFunctional);
